import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Nav, NavItem, NavLink, TabContent, TabPane,
  Spinner, Alert, Badge, Button,
} from 'reactstrap'
import classnames from 'classnames'
import api from '../../../services/api'
import UsersView from './UsersView'
import RulesView from './RulesView'

const EmailSync = () => {
  const { companyId } = useParams()

  const [activeSubTab, setActiveSubTab] = useState('users')
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState(false)
  const [error, setError] = useState(null)

  const loadEnabled = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.get(`/company/${companyId}/email-sync/enabled`)
      if (res.ok) {
        const body = await res.json()
        setEnabled(!!body.enabled)
      } else {
        setError('Failed to load Email Sync state')
      }
    } catch {
      setError('Failed to load Email Sync state')
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => { loadEnabled() }, [loadEnabled])

  const handleToggle = async (next) => {
    if (!next && !window.confirm(
      'Disable Email Sync for this company? Sync will stop pulling SMTP data and pushing aliases. Stored data is kept; toggling back on resumes.'
    )) return
    try {
      setToggling(true)
      setError(null)
      const res = await api.post(`/company/${companyId}/email-sync/enabled`, { enabled: next })
      if (res.ok) {
        const body = await res.json()
        setEnabled(!!body.enabled)
      } else {
        setError('Failed to change Email Sync state')
      }
    } catch {
      setError('Failed to change Email Sync state')
    } finally {
      setToggling(false)
    }
  }

  const toggle = (tab) => {
    if (activeSubTab !== tab) setActiveSubTab(tab)
  }

  if (loading) return <div className="text-center p-4"><Spinner color="primary" /></div>

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <strong>Email Sync:</strong>{' '}
          <Badge color={enabled ? 'success' : 'secondary'}>
            {enabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <div className="small text-muted mt-1">
            {enabled
              ? 'Sync pulls each user\'s SMTP addresses and pushes alias rules into CW contact slots.'
              : 'Pulls and pushes are skipped for this company. Enable to start tracking aliases.'}
          </div>
        </div>
        <Button
          color={enabled ? 'outline-secondary' : 'primary'}
          onClick={() => handleToggle(!enabled)}
          disabled={toggling}
        >
          {toggling ? <Spinner size="sm" /> : (enabled ? 'Disable' : 'Enable')}
        </Button>
      </div>

      {!enabled ? (
        <Alert color="info">
          Email Sync is disabled for this company. Enable it above to start storing users'
          SMTP addresses and configuring rules that push aliases into ConnectWise contact
          email slots.
        </Alert>
      ) : (
        <>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: activeSubTab === 'users' })}
                onClick={() => toggle('users')}
              >
                Users
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeSubTab === 'rules' })}
                onClick={() => toggle('rules')}
              >
                Rules
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={activeSubTab}>
            <TabPane tabId="users" style={{ paddingTop: '20px' }}>
              {activeSubTab === 'users' && <UsersView />}
            </TabPane>
            <TabPane tabId="rules" style={{ paddingTop: '20px' }}>
              {activeSubTab === 'rules' && <RulesView />}
            </TabPane>
          </TabContent>
        </>
      )}
    </div>
  )
}

export default EmailSync
